import i18next from 'i18next';
import { filter, isEmpty } from 'lodash';
import React, { FC, memo, ReactElement, useEffect, useState } from 'react';
import { Col } from 'react-bootstrap';
import { box } from 'tweetnacl';
import { decrypt } from '../../util/crypto';
import { decodeBase64 } from 'tweetnacl-util';
import { formatTimeOrRelative } from '../../util/date-utils';
import { useAuth0 } from '@auth0/auth0-react';
import { useChat } from '../../contexts/chat-context';
import BootstrapAvatar from '../BootstrapAvatar';
import { ChatHeadDto } from '../../../../shared/dtos/chat.dto';

type Props = {
  therapies: ChatHeadDto[],
  selectedTherapy: any;
  handleSelectTherapy: (therapy: ChatHeadDto) => void;
};
const ChatHeadComponent: FC<Props> = ({ therapies, selectedTherapy, handleSelectTherapy }) => {
  const { user } = useAuth0();
  const { tKeyPair, chatHeadMap } = useChat();
  
  const localeOnlineUsers = localStorage.getItem('_online_');
  const onlineUsers = localeOnlineUsers ? localeOnlineUsers.split(',') : [];
  const [therapyList, setTherapyList] = useState<ChatHeadDto[]>([]);
  const [selectedTherapyId, setSelectedTherapyId] = useState(selectedTherapy ? selectedTherapy._id : '');
  
  const searchByName = (searchKey: string): void => {
    if (searchKey && searchKey.length >= 2) {
      const newTherapies = filter(therapies, function (o) {
        return `${o.patientFirstName}${o.patientLastName}`.indexOf(searchKey.toLowerCase()) >= 0;
      });
      setTherapyList(newTherapies);
    } else {
      setTherapyList(therapies);
    }
  };

  const changeChatHead = (therapy: ChatHeadDto): void => {
    if (therapy && therapy.id !== selectedTherapyId) {
      setSelectedTherapyId(therapy.id);
      handleSelectTherapy(therapy);
    }
  };

  useEffect(() => {
    if (therapies && therapies.length > 0) {
      setTherapyList(therapies);
      sortTherapyList(therapies);
    }
  }, [therapies]);
  const renderLastMessage = (therapy: ChatHeadDto): ReactElement => {
    const patientKey = therapy.patientKey;
    const message: string | null = chatHeadMap.get(therapy.id)?.text ?? therapy.lastMessage?.text ?? null;

    if (message && patientKey && tKeyPair) {
      const finalKeyTherapist = box.before(decodeBase64(patientKey), tKeyPair.secretKey);
      let actualMsg = decrypt(finalKeyTherapist, message);
      actualMsg = actualMsg.replace(/<[^>]*>?/gm, '');
      return (
        <div id='small fw-bold'>
          {actualMsg && actualMsg.length > 20 ? actualMsg.substring(0, 20) + '...' : actualMsg}
        </div>
      );
    }

    return <div className='small'></div>;
  };
  const renderDateLabel = (therapy: ChatHeadDto): ReactElement => {
    const patientKey = therapy.patientKey;
    const date: string | null = chatHeadMap.get(therapy.id)?.createdAt ?? therapy.lastMessageDate ?? null;

    if (date && patientKey) {
      return (
        <div className='small'>
          {formatTimeOrRelative(date)}
        </div>);
    }

    return <div className='small'></div>;
  };
  const renderChatHead = (therapy: ChatHeadDto, index: number): ReactElement => {
    const name = `${therapy.patientFirstName} ${therapy.patientLastName}`;
    return <span
      key={'chathead-' + therapy.id + index}
      className={selectedTherapyId === therapy.id ?
        'list-group-item list-group-item-action border-0 chat-head-bgcolor chat-head-selected' :
        'list-group-item list-group-item-action border-0 chat-head-bgcolor'
      }
      onClick={() => { changeChatHead(therapy); }}>
      <div className="d-flex align-items-start">
        <BootstrapAvatar
          name={name}
          size={40}
          color="#707089"
          className="rounded-circle me-1"
          round={true}
        />
        <div className="flex-grow-1 ms-3">
          <span className="fw-bold">{name.length > 25 ? name.substring(0, 25) + '...' : name}</span>
          <span className="float-end">{renderDateLabel(therapy)}</span>
          {renderLastMessage(therapy)}

        </div>
      </div>
    </span>;
  };
  
  const sortTherapyList = (tList: ChatHeadDto[]): void => {
    if (isEmpty(selectedTherapy)) {
      let topTherapy = tList.filter((x) => onlineUsers.indexOf(x.patientId) >= 0 && x.patientAccountId !== user?.sub);
      topTherapy = topTherapy.length === 0 ?
        tList.filter((x) => x.lastMessageDate !== null && onlineUsers
          .indexOf(x.patientId) < 0 && x.patientAccountId !== user?.sub) :
        topTherapy;
      topTherapy = topTherapy.length === 0 ?
        tList.filter((x) => x.lastMessageDate === null && onlineUsers
          .indexOf(x.patientId) < 0 && x.patientAccountId !== user?.sub) :
        topTherapy;
      topTherapy = topTherapy.length === 0 ? tList.filter((x) => x.patientAccountId === user?.sub) : topTherapy;
      
      if (topTherapy && topTherapy.length > 0) {
        handleSelectTherapy(topTherapy[0]);
        setSelectedTherapyId(topTherapy[0].id);
      }
    }
  };
  const renderOnlineUser = (): ReactElement | undefined => {
    const topTherapy =
      therapyList.filter((x) => onlineUsers.indexOf(x.patientId) >= 0 && x.patientAccountId !== user?.sub);
    if (topTherapy && topTherapy.length > 0) {
      return (<>
        {topTherapy.map((therapy, index) => {
          return renderChatHead(therapy, index);
        })}
      </>);
    }
  };
  const renderOfflineUserLastMessage = (): ReactElement => {
    return (<>
      {therapyList
        .filter((x) => x.lastMessageDate === null && onlineUsers
          .indexOf(x.patientId) < 0 && x.patientAccountId !== user?.sub)
        .map((therapy, index) => {
          return renderChatHead(therapy, index);
        })}
    </>);
  };
  const renderOfflineUser = (): ReactElement | undefined => {
    const offlineTherapy = therapyList
      .filter((x) => x.lastMessageDate !== null && onlineUsers
        .indexOf(x.patientId) < 0 && x.patientAccountId !== user?.sub);
    
    if (offlineTherapy && offlineTherapy.length > 0) {
      return (<>
        {offlineTherapy
          .filter((x) => x.lastMessageDate !== null && onlineUsers
            .indexOf(x.patientId) < 0 && x.patientAccountId !== user?.sub)
          .map((therapy, index) => {
            return renderChatHead(therapy, index);
          })}
      </>);
    }
  };
  const renderTherapistUser = (): ReactElement => {
    return (<>
      {therapyList.filter((x) => x.patientAccountId === user?.sub).map((therapy, index) => {
        return renderChatHead(therapy, index);
      })}
    </>);
  };
  return (
    <Col className="col-5 col-lg-4 col-xl-3 col-md-5 border-end chat-head-box pe-0">
      <div className="px-4 d-none d-md-block">
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <input type="text" className="form-control my-3" placeholder={i18next.t('generic.search')} onChange={(e) => searchByName(e.target.value)} />
          </div>
        </div>
      </div>
      {therapyList && therapyList.length === 0 ?
        <><Col md={12} className="loader text-center" key={0}>{i18next.t('chat.noPatientFound')}</Col></>
        : <div className="list-group chat-head-scroll">
          {renderOnlineUser()}
          {renderOfflineUser()}
          {renderOfflineUserLastMessage()}
          {renderTherapistUser()}
          <br /><br /><br /><br />
        </div>}

      <hr className="d-block d-lg-none mt-1 mb-0" />
    </Col>
  );
};

export default memo(ChatHeadComponent);
